import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Globe, Clock, Loader2, Save, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LANG_NAMES = {
    en: 'English', fr: 'French', es: 'Spanish', de: 'German', it: 'Italian',
    pt: 'Portuguese', ja: 'Japanese', zh: 'Chinese', ko: 'Korean', ar: 'Arabic',
    ru: 'Russian', nl: 'Dutch', sv: 'Swedish', da: 'Danish', no: 'Norwegian',
    fi: 'Finnish', pl: 'Polish', cs: 'Czech', tr: 'Turkish', hi: 'Hindi'
};

const STATUS_STYLES = {
    pending: 'bg-amber-500/10 text-amber-400',
    in_progress: 'bg-blue-500/10 text-blue-400',
    completed: 'bg-emerald-500/10 text-emerald-400',
    rejected: 'bg-red-500/10 text-red-400'
};

export default function AdminLanguageRequests() {
    const [requests, setRequests] = useState([]);
    const [pricing, setPricing] = useState({ default_price_per_language: 50, language_prices: {}, lead_time_note: '3-6 weeks per attraction' });
    const [loading, setLoading] = useState(true);
    const [savingPricing, setSavingPricing] = useState(false);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [reqRes, priceRes] = await Promise.all([
                axios.get(`${API}/api/admin/language-requests`, { withCredentials: true }),
                axios.get(`${API}/api/admin/settings/language-pricing`, { withCredentials: true })
            ]);
            setRequests(reqRes.data);
            setPricing(priceRes.data);
        } catch {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const savePricing = async () => {
        setSavingPricing(true);
        try {
            await axios.put(`${API}/api/admin/settings/language-pricing`, pricing, { withCredentials: true });
            toast.success('Language pricing saved');
        } catch {
            toast.error('Failed to save pricing');
        } finally {
            setSavingPricing(false);
        }
    };

    const updateStatus = async (requestId, newStatus) => {
        try {
            await axios.put(`${API}/api/admin/language-requests/${requestId}`, { status: newStatus }, { withCredentials: true });
            toast.success(`Request marked as ${newStatus.replace('_', ' ')}`);
            fetchAll();
        } catch {
            toast.error('Failed to update status');
        }
    };

    const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

    const stats = {
        pending: requests.filter(r => r.status === 'pending').length,
        in_progress: requests.filter(r => r.status === 'in_progress').length,
        completed: requests.filter(r => r.status === 'completed').length
    };

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8 max-w-6xl">
                <div>
                    <h2 className="font-outfit text-2xl font-semibold text-white">Language Requests</h2>
                    <p className="text-muted-foreground text-sm mt-1">Manage operator language add-on requests and pricing</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-amber-500/5 border-amber-500/20">
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
                            <p className="text-xs text-muted-foreground">Pending</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-blue-500/5 border-blue-500/20">
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-blue-400">{stats.in_progress}</p>
                            <p className="text-xs text-muted-foreground">In Progress</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-emerald-500/5 border-emerald-500/20">
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-emerald-400">{stats.completed}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Pricing Config */}
                <Card className="bg-[#121620] border-white/10">
                    <CardHeader>
                        <CardTitle className="text-white font-outfit flex items-center gap-2 text-lg">
                            <Globe className="w-5 h-5 text-gold" /> Language Pricing
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Default Price per Language (credits)</Label>
                                <Input
                                    type="number"
                                    value={pricing.default_price_per_language}
                                    onChange={(e) => setPricing(p => ({ ...p, default_price_per_language: parseFloat(e.target.value) || 50 }))}
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="default-lang-price-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Lead Time Note</Label>
                                <Input
                                    value={pricing.lead_time_note}
                                    onChange={(e) => setPricing(p => ({ ...p, lead_time_note: e.target.value }))}
                                    className="bg-[#0A0D14] border-white/10"
                                    data-testid="lead-time-note-input"
                                />
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">You can set per-language overrides below. Languages not listed use the default price.</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {Object.entries(LANG_NAMES).slice(4).map(([code, name]) => (
                                <div key={code} className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground w-16 uppercase">{code}</span>
                                    <Input
                                        type="number"
                                        placeholder={String(pricing.default_price_per_language)}
                                        value={pricing.language_prices?.[code] || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setPricing(p => ({
                                                ...p,
                                                language_prices: {
                                                    ...p.language_prices,
                                                    [code]: val ? parseFloat(val) : undefined
                                                }
                                            }));
                                        }}
                                        className="bg-[#0A0D14] border-white/10 h-8 text-xs"
                                    />
                                </div>
                            ))}
                        </div>
                        <Button onClick={savePricing} disabled={savingPricing} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="save-lang-pricing-btn">
                            {savingPricing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Save Language Pricing
                        </Button>
                    </CardContent>
                </Card>

                {/* Requests Table */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-white font-medium">Requests</h3>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-40 bg-[#121620] border-white/10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121620] border-white/10">
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <Loader2 className="w-6 h-6 animate-spin text-gold" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 bg-[#121620] rounded-xl border border-white/10">
                            <Globe className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground">No language requests</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                    <TableHead className="text-muted-foreground">Operator</TableHead>
                                    <TableHead className="text-muted-foreground">Attraction</TableHead>
                                    <TableHead className="text-muted-foreground">Language</TableHead>
                                    <TableHead className="text-muted-foreground">Paid</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground">Date</TableHead>
                                    <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((req) => (
                                    <TableRow key={req.id} className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-white text-sm">{req.operator_name}</TableCell>
                                        <TableCell className="text-muted-foreground text-sm">{req.attraction_title}</TableCell>
                                        <TableCell>
                                            <span className="px-2 py-0.5 rounded-full text-xs bg-gold/10 text-gold uppercase">
                                                {LANG_NAMES[req.language] || req.language}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-gold text-sm">{req.price_paid}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLES[req.status]}`}>
                                                {req.status?.replace('_', ' ')}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-xs">{new Date(req.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center gap-1 justify-end">
                                                {req.status === 'pending' && (
                                                    <>
                                                        <Button variant="ghost" size="sm" onClick={() => updateStatus(req.id, 'in_progress')} className="text-blue-400 hover:text-blue-300 gap-1 text-xs" data-testid={`start-${req.id}`}>
                                                            <ArrowRight className="w-3 h-3" /> Start
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => updateStatus(req.id, 'rejected')} className="text-red-400 hover:text-red-300" data-testid={`reject-${req.id}`}>
                                                            <XCircle className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                {req.status === 'in_progress' && (
                                                    <Button variant="ghost" size="sm" onClick={() => updateStatus(req.id, 'completed')} className="text-emerald-400 hover:text-emerald-300 gap-1 text-xs" data-testid={`complete-${req.id}`}>
                                                        <CheckCircle className="w-3 h-3" /> Complete
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}
